import { useEffect } from 'react'
import { setCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { useLegacyEffect } from '@/molecules/utils'
import { GuildGeogatedBenefitsPrompt } from '@/organisms/GuildGeogatedBenefitsPrompt'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getContentfulClient } from '@/services/ApolloClient'
import { getCollection, GUILD_PREDICTS_WINNERS_ENTRY_ID } from '@/services/CmsService/Collection'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import {
  getExplainerLayout,
  getGuildSignupPageBySlug,
  getTestimonialSection,
  getTicketsSection,
  getGuildPricingTicketsOption,
  GuildPricingTicketsOption,
  getFeaturedSection,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { FETCH_USER_MEMBERSHIP, hasOnlyGiftedMembership } from '@/services/MembershipService/MembershipService'
import { isSubPurchaseBlocked } from '@/services/MembershipService/membership-status'
import { getProjectLogoUrl } from '@/services/ProjectsService/ProjectsService'
import { getGuildSale, GuildSale } from '@/services/SaleService'
import {
  Collection,
  GuildFeaturedSection,
  GuildSignupExplainerLayout,
  GuildSignupTicketsSection,
  GuildTestimonialSection,
  Maybe,
} from '@/types/codegen-contentful'
import { FetchUserMembershipQuery } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildStatus } from '@/utils/users/usersServer'
import { GuildSignupView } from '@/views/GuildSignupView'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildJoinProps {
  projectLogoUrl: string | null
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  sale: GuildSale | null
  saleNameOverride?: string | null
  slug: string
  guildFeaturedSection?: GuildFeaturedSection | null
  votingPhoneImageUrl?: string | null
  guildTestimonialSection?: GuildTestimonialSection
  guildTicketsSection?: GuildSignupTicketsSection
  guildPredictsWinners?: Collection
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments?: ServerExperiments
  region: string | null
  isDarkMode?: boolean
  guildPricingTicketsOption?: Maybe<GuildPricingTicketsOption>
}

export const GuildJoinPage: NextPageWithLayout<GuildJoinProps> = ({
  projectLogoUrl,
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildExplainerLayout,
  layout,
  locale,
  projectSlugs,
  promotion,
  sale,
  slug,
  votingPhoneImageUrl,
  guildTestimonialSection,
  guildTicketsSection,
  guildPredictsWinners,
  hasUser,
  logo,
  experiments,
  guildFeaturedSection,
  region,
  guildPricingTicketsOption,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    writeToSessionStorage('pageSlug', slug)
    writeToSessionStorage('guildCheckoutOrigin', `/guild/join`)
    projectSlugsAttribution(projectSlugs)
  }, [slug, projectSlugs])

  useStickyAttribution('guild-attribution')

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'angelStudiosGuildJoinInfoMetaDescriptionV3',
            'Join the Angel Guild to vote on upcoming shows, get free tickets to theatrical releases, early access, and merchandise discounts.',
          ),
          locale,
          path: `${paths.guild.join}/${slug}`,
          title: t('angelGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel'),
        })}
      />
      <GuildSignupContextProvider
        projectLogoUrl={projectLogoUrl}
        guildTestimonialSection={guildTestimonialSection}
        guildTicketsSection={guildTicketsSection}
        emphasis={emphasis}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        votingPhoneImageUrl={votingPhoneImageUrl}
        logo={logo}
        experiments={experiments}
        guildPredictsWinners={guildPredictsWinners}
        region={region}
        guildPricingTicketsOption={guildPricingTicketsOption}
        guildFeaturedSection={guildFeaturedSection}
        showSocialProof
      >
        <GuildSignupView hasUser={hasUser} />
      </GuildSignupContextProvider>
      <MarketingVideoPopup ctaHref={'#plan-selection'} ctaText={t('joinTheGuild', 'Join the Guild')} />
      <GuildGeogatedBenefitsPrompt />
    </div>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, next404, nextRedirect, preview, query, req, res, userId, region }) => {
    const slug = 'angel'
    const isSupportLink = query.special === 'last-chance'

    const opts = { locale, preview }
    const contentfulClient = getContentfulClient({ preview, locale })
    const [pageData, guildStatus, membershipData] = await Promise.all([
      getGuildSignupPageBySlug({ contentfulClient, slug, opts }),
      getGuildStatus(federationClient),
      userId
        ? federationClient
            .query<FetchUserMembershipQuery>({ query: FETCH_USER_MEMBERSHIP, context: { requireAuth: true } })
            .then((result) => result.data?.user?.myMembership ?? null)
            .catch(() => null)
        : Promise.resolve(null),
    ])

    if (!pageData) {
      return next404()
    }

    if (pageData.redirectTo) {
      return {
        redirect: {
          destination: pageData.redirectTo,
          permanent: false,
        },
      }
    }

    const emphasis = pageData.emphasis
    const hasGiftedOnly = hasOnlyGiftedMembership(membershipData)

    if (isSubPurchaseBlocked(membershipData)) {
      return nextRedirect(paths.account.guildMembership)
    }

    if (shouldRedirectToDashboard(guildStatus, emphasis) && !hasGiftedOnly) {
      return {
        redirect: {
          destination: pageData.guildMemberHref ?? `/guild#${emphasis}`,
          permanent: false,
        },
      }
    }

    const layout = pageData.layout
    const guildExplainerLayout = getExplainerLayout(pageData)
    const guildTestimonialSection = getTestimonialSection(pageData)
    const guildTicketsSection = getTicketsSection(pageData)
    const guildFeaturedSection = getFeaturedSection(pageData)

    const projectSlug = pageData?.project?.slug

    const [
      sale,
      promotion,
      guildPricingTicketsOption,
      guildPredictsWinners,
      projectLogoUrl,
      experiments,
      translations,
    ] = await Promise.all([
      getGuildSale({ opts: { locale, preview }, federationClient }),
      getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
      getGuildPricingTicketsOption({ contentfulClient, opts }),
      getCollection({ locale, preview }, GUILD_PREDICTS_WINNERS_ENTRY_ID),
      projectSlug ? getProjectLogoUrl(projectSlug, federationClient) : Promise.resolve(null),
      runExperiments({ req, res }),
      loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo', 'watch']),
    ])

    return {
      props: {
        projectLogoUrl,
        emphasis,
        guildExplainerLayout,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale,
        saleNameOverride: pageData.saleNameOverride ?? null,
        slug,
        votingPhoneImageUrl: pageData.votingPhoneImageUrl ?? null,
        logo: pageData?.project?.logo ?? null,
        guildTestimonialSection,
        guildTicketsSection,
        guildFeaturedSection,
        guildPredictsWinners: guildPredictsWinners ?? null,
        hasUser: Boolean(userId),
        experiments,
        region,
        isDarkMode: true,
        guildPricingTicketsOption,
        ...translations,
      },
    }
  },
)

GuildJoinPage.getLayout = (page, pageProps) => {
  return (
    <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)} saleNameOverride={pageProps?.saleNameOverride}>
      {page}
    </DefaultLayout>
  )
}

export default GuildJoinPage

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: GuildSignupEmphasis): boolean {
  if (!guildStatus) return false
  if (emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) return true
  if (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess) return true
  return false
}

async function runExperiments({
  req,
  res,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = [
    { experimentKey: 'thousands-joined-follow-up', defaultValue: 'control' },
    { experimentKey: 'coupon-test-member-selection', defaultValue: 'control' },
  ]
  return await runExperimentsOnServer(req, res, expConfigs)
}
